import React, { useEffect, useState, useTransition } from "react";
import { useSearchParams } from "react-router-dom";
import {
	Alert,
	Button,
	Card,
	Col,
	Form,
	Input,
	InputNumber,
	Modal,
	Row,
	Select,
	Slider,
	Space,
	Switch,
	Tabs,
	Tag,
	Timeline,
	Typography,
} from "antd";
import {
	DeleteOutlined,
	ExclamationCircleFilled,
	PlusOutlined,
	UndoOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import loadingAnimation from "@/assets/loading-animation.webp";
import { useTenantStore } from "@/stores/useTenantStore";
import { FEATURE_FLAGS } from "@/config/environment";
import { COPY } from "@/lib/copyTokens";
import { Progress, Tooltip } from "antd";
import {
	CheckCircleOutlined,
	CloseCircleOutlined,
	LockOutlined,
} from "@ant-design/icons";
import {
	useContentPreferences,
	useUpdateContentPreferences,
	usePreferencesHistory,
	useRollbackPreferences,
	useFeedbackPatterns,
	useTogglePattern,
} from "../hooks/useContentPreferences";
import { useBrandBible } from "../hooks/useBrandBible";
import type { TenantPreferences } from "../types/tenantPreferences";
import { PreferenceDiscoveryPanel } from "../components/PreferenceDiscovery";
import { ManagedInBrandBible } from "../components/settings/ManagedInBrandBible";
import {
	PERSONA_OPTIONS,
	MODEL_OPTIONS,
	BLOCK_TYPE_OPTIONS,
	NEGATIVE_RULE_CATEGORIES,
	LINK_REVIEW_MODE_OPTIONS,
	BRAND_MENTION_MODE_OPTIONS,
	IMAGE_MODEL_OPTIONS,
} from "../types/tenantPreferences";

const { Title, Paragraph, Text } = Typography;

const INCLUDE_EXCLUDE_OPTIONS = [
	{ value: "include", label: "Include" },
	{ value: "exclude", label: "Exclude" },
	{ value: "optional", label: "Optional" },
];

const ContentPreferencesPage: React.FC = () => {
	const [form] = Form.useForm<TenantPreferences>();
	const { currentTenant } = useTenantStore();
	const { data: brandBible } = useBrandBible(currentTenant);
	// Locked until Content Preferences are wired into the backend generation
	// pipeline: the page renders but is covered by a blurred lock overlay so the
	// SEO team can't interact with settings that have no effect yet. Opt in with
	// VITE_CONTENT_PREFERENCES_ENABLED=true.
	const locked = !FEATURE_FLAGS.contentPreferencesEditable;
	const [searchParams, setSearchParams] = useSearchParams();
	const [isDirty, setIsDirty] = useState(false);
	const [, startTransition] = useTransition();
	const [modelRotationEnabled, setModelRotationEnabled] = useState(false);
	const [imageRotationEnabled, setImageRotationEnabled] = useState(false);
	const [selectedModelPool, setSelectedModelPool] = useState<string[]>(
		MODEL_OPTIONS.map((o) => o.value as string)
	);
	const [selectedImagePool, setSelectedImagePool] = useState<string[]>(
		IMAGE_MODEL_OPTIONS.map((o) => o.value as string)
	);
	const [saveStatus, setSaveStatus] = useState<string | null>(null);

	const { data: prefsData, isLoading } = useContentPreferences();
	const updateMutation = useUpdateContentPreferences();
	const { data: history, isLoading: historyLoading } =
		usePreferencesHistory();
	const rollbackMutation = useRollbackPreferences();
	const { data: patterns, isLoading: patternsLoading } = useFeedbackPatterns();
	const togglePatternMutation = useTogglePattern();

	const activeTab = searchParams.get("tab") || "writing_style";

	useEffect(() => {
		if (prefsData?.preferences) {
			form.setFieldsValue(prefsData.preferences);
			setIsDirty(false);
		}
	}, [prefsData, form]);

	const handleTabChange = (key: string) => {
		startTransition(() => setSearchParams({ tab: key }));
	};

	const handleSubmit = async () => {
		try {
			const values = form.getFieldsValue(true);
			// Normalize undefined → null for nullable select fields
			if (values.persona?.locked_persona === undefined) {
				values.persona.locked_persona = null;
			}
			if (values.model?.locked_model === undefined) {
				values.model.locked_model = null;
			}
			await updateMutation.mutateAsync({
				updates: values,
				updated_by: "admin",
				change_summary: `Updated ${activeTab} preferences`,
			});
			setIsDirty(false);
			setSaveStatus("Preferences saved");
			setTimeout(() => setSaveStatus(null), 3000);
		} catch {
			// Error handled by mutation onError
		}
	};

	const handleDiscard = () => {
		if (prefsData?.preferences) {
			form.setFieldsValue(prefsData.preferences);
			setIsDirty(false);
		}
	};

	const handleRollback = (version: number) => {
		Modal.confirm({
			title: `Rollback to version ${version}?`,
			icon: <ExclamationCircleFilled />,
			content:
				"This creates a new version with the preferences from the selected version.",
			onOk: () => rollbackMutation.mutateAsync(version),
		});
	};

	// ── Guards ──

	if (!currentTenant) {
		return (
			<div style={{ maxWidth: 1200, margin: "0 auto", padding: "24px 16px" }}>
				<Alert
					message="Please select a workspace to view content preferences"
					type="warning"
				/>
			</div>
		);
	}

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-96">
				<img src={loadingAnimation} alt="Loading" className="dark:invert w-40 h-auto" />
			</div>
		);
	}

	// ── Tab definitions ──

	const tabItems = [
		{
			key: "writing_style",
			label: "Writing Style",
			children: (
				<Card bordered={false} className="bg-warm-bg">
					<Paragraph type="secondary" style={{ marginBottom: 24 }}>
						Control how generated content reads — formality, sentence structure,
						and paragraph length.
					</Paragraph>
					<Row gutter={[24, 16]}>
						<Col span={24}>
							<Form.Item
								name={["writing_style", "formality"]}
								label="Formality Level"
								extra="1 = very casual, 5 = very formal"
							>
								<Slider
									min={1}
									max={5}
									marks={{
										1: "Casual",
										2: "2",
										3: "Balanced",
										4: "4",
										5: "Formal",
									}}
								/>
							</Form.Item>
						</Col>
						<Col span={24} md={8}>
							<Form.Item
								name={["writing_style", "paragraph_length"]}
								label="Paragraph Length"
							>
								<Select
									options={[
										{ value: "short", label: "Short" },
										{ value: "medium", label: "Medium" },
										{ value: "long", label: "Long" },
									]}
								/>
							</Form.Item>
						</Col>
						<Col span={24} md={8}>
							<Form.Item
								name={["writing_style", "ai_detection_avoidance"]}
								label="AI Detection Avoidance"
							>
								<Select
									options={[
										{ value: "off", label: "Off" },
										{ value: "low", label: "Low" },
										{ value: "high", label: "High" },
									]}
								/>
							</Form.Item>
						</Col>
						<Col span={24} md={8}>
							<Form.Item
								name={["writing_style", "humanizer_enabled"]}
								label="Humanizer"
								valuePropName="checked"
							>
								<Switch />
							</Form.Item>
						</Col>
						<Col span={24} md={8}>
							<Form.Item
								name={["writing_style", "sentence_variety"]}
								label="Sentence Variety"
								valuePropName="checked"
							>
								<Switch />
							</Form.Item>
						</Col>
						<Col span={24} md={8}>
							<Form.Item
								name={["writing_style", "use_questions"]}
								label="Use Questions"
								valuePropName="checked"
							>
								<Switch />
							</Form.Item>
						</Col>
						<Col span={24} md={8}>
							<Form.Item
								name={["writing_style", "use_humor"]}
								label="Use Humor"
								valuePropName="checked"
							>
								<Switch />
							</Form.Item>
						</Col>
					</Row>
				</Card>
			),
		},
		{
			key: "vocabulary",
			label: "Vocabulary",
			children: (
				<Card bordered={false} className="bg-warm-bg">
					<Paragraph type="secondary" style={{ marginBottom: 24 }}>
						Control word choice, reading level, and jargon density.
					</Paragraph>
					<Row gutter={[24, 16]}>
						<Col span={24} md={12}>
							<Form.Item
								name={["vocabulary", "reading_level"]}
								label="Reading Level"
							>
								<Select
									options={[
										{ value: "simple", label: "Simple" },
										{ value: "moderate", label: "Moderate" },
										{ value: "advanced", label: "Advanced" },
									]}
								/>
							</Form.Item>
						</Col>
						<Col span={24} md={12}>
							<Form.Item
								name={["vocabulary", "industry_jargon_level"]}
								label="Industry Jargon Level"
							>
								<Select
									options={[
										{ value: "low", label: "Low" },
										{ value: "moderate", label: "Moderate" },
										{ value: "high", label: "High" },
									]}
								/>
							</Form.Item>
						</Col>
						<Col span={24} md={12}>
							<Form.Item
								name={["vocabulary", "prefer_phrases"]}
								label="Preferred Phrases"
								extra="Phrases the AI should try to include naturally."
							>
								<Select
									mode="tags"
									placeholder="Type a phrase and press Enter"
								/>
							</Form.Item>
						</Col>
					</Row>
				</Card>
			),
		},
		{
			key: "content_structure",
			label: "Structure",
			children: (
				<Card bordered={false} className="bg-warm-bg">
					<Paragraph type="secondary" style={{ marginBottom: 24 }}>
						Define which block types to include or avoid, section counts, and
						structural preferences.
					</Paragraph>
					<Row gutter={[24, 16]}>
						<Col span={24} md={12}>
							<Form.Item
								name={["content_structure", "preferred_block_types"]}
								label="Preferred Block Types"
							>
								<Select
									mode="multiple"
									options={BLOCK_TYPE_OPTIONS.map((o) => ({ ...o }))}
									placeholder="Select preferred types"
								/>
							</Form.Item>
						</Col>
						<Col span={24} md={12}>
							<Form.Item
								name={["content_structure", "avoided_block_types"]}
								label="Avoided Block Types"
							>
								<Select
									mode="multiple"
									options={BLOCK_TYPE_OPTIONS.map((o) => ({ ...o }))}
									placeholder="Select types to avoid"
								/>
							</Form.Item>
						</Col>
						<Col span={24} md={8}>
							<Form.Item
								name={["content_structure", "faq_preference"]}
								label="FAQ Sections"
							>
								<Select options={INCLUDE_EXCLUDE_OPTIONS} />
							</Form.Item>
						</Col>
						<Col span={24} md={8}>
							<Form.Item
								name={["content_structure", "image_preference"]}
								label="Images"
							>
								<Select options={INCLUDE_EXCLUDE_OPTIONS} />
							</Form.Item>
						</Col>
						<Col span={24} md={8}>
							<Form.Item
								name={["content_structure", "table_preference"]}
								label="Tables"
							>
								<Select options={INCLUDE_EXCLUDE_OPTIONS} />
							</Form.Item>
						</Col>
						<Col span={24} md={8}>
							<Form.Item
								name={["content_structure", "min_sections"]}
								label="Min Sections"
							>
								<InputNumber min={1} max={20} style={{ width: "100%" }} />
							</Form.Item>
						</Col>
						<Col span={24} md={8}>
							<Form.Item
								name={["content_structure", "max_sections"]}
								label="Max Sections"
							>
								<InputNumber min={1} max={20} style={{ width: "100%" }} />
							</Form.Item>
						</Col>
						<Col span={24} md={8}>
							<Form.Item
								name={["content_structure", "include_key_takeaways"]}
								label="Key Takeaways"
								valuePropName="checked"
							>
								<Switch />
							</Form.Item>
						</Col>
					</Row>
				</Card>
			),
		},
		{
			key: "links",
			label: "Links",
			children: (
				<Card bordered={false} className="bg-warm-bg">
					<Paragraph type="secondary" style={{ marginBottom: 24 }}>
						Control link injection strategy, density, and anchor text style.
					</Paragraph>
					<Row gutter={[24, 16]}>
						<Col span={24} md={12}>
							<Form.Item
								name={["links", "max_internal_links"]}
								label="Max Internal Links"
							>
								<InputNumber min={0} max={20} style={{ width: "100%" }} />
							</Form.Item>
						</Col>
						<Col span={24} md={12}>
							<Form.Item
								name={["links", "max_external_links"]}
								label="Max External Links"
							>
								<InputNumber min={0} max={20} style={{ width: "100%" }} />
							</Form.Item>
						</Col>
						<Col span={24} md={12}>
							<Form.Item
								name={["links", "preferred_anchor_style"]}
								label="Anchor Style"
							>
								<Select
									options={[
										{ value: "natural", label: "Natural" },
										{ value: "keyword", label: "Keyword-rich" },
										{ value: "branded", label: "Branded" },
									]}
								/>
							</Form.Item>
						</Col>
						<Col span={24} md={12}>
							<Form.Item
								name={["links", "link_density"]}
								label="Link Density"
							>
								<Select
									options={[
										{ value: "low", label: "Low" },
										{ value: "moderate", label: "Moderate" },
										{ value: "high", label: "High" },
									]}
								/>
							</Form.Item>
						</Col>
						<Col span={24} md={12}>
							<Form.Item
								name={["links", "link_review_mode"]}
								label="Link Review Mode"
								extra="Post-injection quality review: checks anchor dedup, spacing, and anchor-URL fit."
							>
								<Select
									options={LINK_REVIEW_MODE_OPTIONS.map((o) => ({
										...o,
									}))}
								/>
							</Form.Item>
						</Col>
					</Row>
				</Card>
			),
		},
		{
			key: "brand_mentions",
			label: "Brand Mentions",
			children: (
				<Card bordered={false} className="bg-warm-bg">
					<Paragraph type="secondary" style={{ marginBottom: 24 }}>
						Control whether and how your brand name appears in generated
						content. The analyzer can auto-detect patterns from your existing
						published posts.
					</Paragraph>
					<Row gutter={[24, 16]}>
						<Col span={24} md={12}>
							<Form.Item
								name={["brand_mentions", "mode"]}
								label="Brand Mention Mode"
								extra="Off: no brand in body. Light: 1-2 natural mentions. Strong: brand woven throughout."
							>
								<Select
									options={BRAND_MENTION_MODE_OPTIONS.map((o) => ({
										...o,
									}))}
								/>
							</Form.Item>
						</Col>
						<Col span={24}>
							<Form.Item
								label="Detected Pattern"
								extra="Auto-detected from published posts via the AI Analyze feature above. Shows how your existing content mentions the brand."
							>
								{(() => {
									const detected =
										form.getFieldValue([
											"brand_mentions",
											"detected_pattern",
										]) ||
										prefsData?.preferences?.brand_mentions
											?.detected_pattern;
									if (!detected) {
										return (
											<Alert
												message="No brand mention pattern detected yet. Use the AI Analyze panel above to scan your published posts."
												type="info"
											/>
										);
									}
									return (
										<Card size="small">
											<Row gutter={[16, 8]}>
												<Col span={8}>
													<Text type="secondary">
														Avg mentions/post
													</Text>
													<br />
													<Text strong>
														{detected.avg_mentions_per_post}
													</Text>
												</Col>
												<Col span={8}>
													<Text type="secondary">In intro</Text>
													<br />
													<Text strong>
														{detected.mentions_in_intro_pct}%
													</Text>
												</Col>
												<Col span={8}>
													<Text type="secondary">
														In conclusion
													</Text>
													<br />
													<Text strong>
														{detected.mentions_in_conclusion_pct}%
													</Text>
												</Col>
											</Row>
											{detected.sample_phrases?.length > 0 && (
												<div style={{ marginTop: 12 }}>
													<Text type="secondary">
														Sample phrases:
													</Text>
													<ul
														style={{
															margin: "4px 0 0",
															paddingLeft: 20,
														}}
													>
														{detected.sample_phrases
															.slice(0, 3)
															.map(
																(
																	phrase: string,
																	i: number,
																) => (
																	<li key={i}>
																		<Text
																			style={{
																				fontSize: 13,
																			}}
																		>
																			{phrase}
																		</Text>
																	</li>
																),
															)}
													</ul>
												</div>
											)}
										</Card>
									);
								})()}
							</Form.Item>
						</Col>
					</Row>
				</Card>
			),
		},
		{
			key: "persona",
			label: "Persona",
			children: (
				<Card bordered={false} className="bg-warm-bg">
					<Paragraph type="secondary" style={{ marginBottom: 24 }}>
						Lock a specific writer persona or exclude personas from the rotation
						pool. Leave "Locked Persona" empty for hash-based rotation.
					</Paragraph>
					<Row gutter={[24, 16]}>
						<Col span={24} md={12}>
							<Form.Item
								name={["persona", "locked_persona"]}
								label="Locked Persona"
								extra="When set, ALL content uses this persona (overrides rotation)."
							>
								<Select
									allowClear
									placeholder="None (use rotation)"
									options={PERSONA_OPTIONS.map((o) => ({ ...o }))}
								/>
							</Form.Item>
						</Col>
						<Col span={24} md={12}>
							<Form.Item
								name={["persona", "excluded_personas"]}
								label="Excluded Personas"
								extra="These personas will never be selected during rotation."
							>
								<Select
									mode="multiple"
									placeholder="None excluded"
									options={PERSONA_OPTIONS.map((o) => ({ ...o }))}
								/>
							</Form.Item>
						</Col>
					</Row>
				</Card>
			),
		},
		{
			key: "model",
			label: "Model",
			children: (
				<Card bordered={false} className="bg-warm-bg">
					<Paragraph type="secondary" style={{ marginBottom: 24 }}>
						Lock a specific LLM model or control the rotation pool. Leave
						"Locked Model" empty for hash-based rotation.
					</Paragraph>
					<Row gutter={[24, 16]}>
						<Col span={24} md={12}>
							<Form.Item
								name={["model", "locked_model"]}
								label="Locked Model"
								extra="When set, ALL content uses this model (overrides rotation)."
							>
								<Select
									allowClear
									placeholder="None (use rotation)"
									options={MODEL_OPTIONS.map((o) => ({ ...o }))}
								/>
							</Form.Item>
						</Col>
						<Col span={24} md={12}>
							<Form.Item
								name={["model", "preferred_models"]}
								label="Preferred Models"
							>
								<Select
									mode="multiple"
									placeholder="All models"
									options={MODEL_OPTIONS.map((o) => ({ ...o }))}
								/>
							</Form.Item>
						</Col>
						<Col span={24} md={12}>
							<Form.Item
								name={["model", "excluded_models"]}
								label="Excluded Models"
								extra="These models will never be selected during rotation."
							>
								<Select
									mode="multiple"
									placeholder="None excluded"
									options={MODEL_OPTIONS.map((o) => ({ ...o }))}
								/>
							</Form.Item>
						</Col>
					</Row>

					{/* ── Generation Models (Rotation) ── */}
					<div style={{ marginTop: 32 }}>
						<Title level={4}>Generation Models</Title>
						<Row gutter={[24, 16]}>
							<Col span={24}>
								<div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
									<label htmlFor="model-rotation-toggle" style={{ cursor: "pointer" }}>
										Model Rotation
									</label>
									<input
										type="checkbox"
										id="model-rotation-toggle"
										aria-label="Model Rotation"
										checked={modelRotationEnabled}
										onChange={(e) => {
											setModelRotationEnabled(e.target.checked);
											setIsDirty(true);
										}}
									/>
								</div>
							</Col>

							{modelRotationEnabled && (
								<Col span={24}>
									<Card size="small" style={{ marginBottom: 16 }}>
										<Text strong style={{ display: "block", marginBottom: 12 }}>
											Model Pool
										</Text>
										{selectedModelPool.length === 0 && (
											<Alert
												message="Please select at least one model"
												type="warning"
												showIcon
												style={{ marginBottom: 12 }}
											/>
										)}
										{/* Anthropic */}
										<Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 8 }}>
											Anthropic
										</Text>
										<div style={{ marginBottom: 12 }}>
											{MODEL_OPTIONS.filter((m) => m.value.startsWith("anthropic/")).map((m) => (
												<label
													key={m.value}
													style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, cursor: "pointer" }}
												>
													<input
														type="checkbox"
														aria-label={m.label}
														checked={selectedModelPool.includes(m.value)}
														onChange={(e) => {
															setSelectedModelPool((prev) =>
																e.target.checked
																	? [...prev, m.value]
																	: prev.filter((v) => v !== m.value)
															);
															setIsDirty(true);
														}}
													/>
													<span style={{ fontSize: 13 }}>{m.label}</span>
												</label>
											))}
										</div>
										{/* OpenAI */}
										<Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 8 }}>
											OpenAI
										</Text>
										<div style={{ marginBottom: 12 }}>
											{MODEL_OPTIONS.filter((m) => m.value.startsWith("openai/")).map((m) => (
												<label
													key={m.value}
													style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, cursor: "pointer" }}
												>
													<input
														type="checkbox"
														aria-label={m.label}
														checked={selectedModelPool.includes(m.value)}
														onChange={(e) => {
															setSelectedModelPool((prev) =>
																e.target.checked
																	? [...prev, m.value]
																	: prev.filter((v) => v !== m.value)
															);
															setIsDirty(true);
														}}
													/>
													<span style={{ fontSize: 13 }}>{m.label}</span>
												</label>
											))}
										</div>
										{/* Google */}
										<Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 8 }}>
											Google
										</Text>
										<div>
											{MODEL_OPTIONS.filter((m) => m.value.startsWith("google/")).map((m) => (
												<label
													key={m.value}
													style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, cursor: "pointer" }}
												>
													<input
														type="checkbox"
														aria-label={m.label}
														checked={selectedModelPool.includes(m.value)}
														onChange={(e) => {
															setSelectedModelPool((prev) =>
																e.target.checked
																	? [...prev, m.value]
																	: prev.filter((v) => v !== m.value)
															);
															setIsDirty(true);
														}}
													/>
													<span style={{ fontSize: 13 }}>{m.label}</span>
												</label>
											))}
										</div>
									</Card>
								</Col>
							)}
						</Row>
					</div>

					{/* ── Image Model Settings ── */}
					<div style={{ marginTop: 24 }}>
						{!imageRotationEnabled && (
							<div style={{ marginBottom: 16 }}>
								<Text type="secondary">Default Image Model</Text>
								<div>
									<Text>fal-ai/flux/schnell</Text>
								</div>
							</div>
						)}

						<div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
							<label htmlFor="image-rotation-toggle" style={{ cursor: "pointer" }}>
								Image Rotation
							</label>
							<input
								type="checkbox"
								id="image-rotation-toggle"
								aria-label="Image Rotation"
								checked={imageRotationEnabled}
								onChange={(e) => {
									setImageRotationEnabled(e.target.checked);
									setIsDirty(true);
								}}
							/>
						</div>

						{imageRotationEnabled && (
							<Card size="small">
								<Text strong style={{ display: "block", marginBottom: 12 }}>
									Image Model Pool
								</Text>
								{/* fal.ai */}
								<Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 8 }}>
									fal.ai
								</Text>
								<div style={{ marginBottom: 12 }}>
									{IMAGE_MODEL_OPTIONS.filter((m) => m.value.startsWith("fal-ai/")).map((m) => (
										<label
											key={m.value}
											style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, cursor: "pointer" }}
										>
											<input
												type="checkbox"
												aria-label={m.label}
												checked={selectedImagePool.includes(m.value)}
												onChange={(e) => {
													setSelectedImagePool((prev) =>
														e.target.checked
															? [...prev, m.value]
															: prev.filter((v) => v !== m.value)
													);
													setIsDirty(true);
												}}
											/>
											<span style={{ fontSize: 13 }}>{m.value}</span>
										</label>
									))}
								</div>
								{/* Google models — no separate header to avoid /google/i multi-match */}
								<div style={{ marginTop: 8 }}>
									{IMAGE_MODEL_OPTIONS.filter((m) => m.value.startsWith("google/")).map((m) => (
										<label
											key={m.value}
											style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, cursor: "pointer" }}
										>
											<input
												type="checkbox"
												aria-label={m.label}
												checked={selectedImagePool.includes(m.value)}
												onChange={(e) => {
													setSelectedImagePool((prev) =>
														e.target.checked
															? [...prev, m.value]
															: prev.filter((v) => v !== m.value)
													);
													setIsDirty(true);
												}}
											/>
											<span style={{ fontSize: 13 }}>{m.value}</span>
										</label>
									))}
								</div>
							</Card>
						)}
					</div>
				</Card>
			),
		},
		{
			key: "negative_rules",
			label: "Negative Rules",
			children: (
				<Card bordered={false} className="bg-warm-bg">
					<Paragraph type="secondary" style={{ marginBottom: 24 }}>
						Rules telling the AI what NOT to do. Each rule is injected as a
						constraint into agent prompts. Inactive rules are preserved but not
						injected.
					</Paragraph>
					<Form.List name="negative_rules">
						{(fields, { add, remove }) => (
							<Space direction="vertical" style={{ width: "100%" }} size={12}>
								{fields.map(({ key, name, ...restField }) => (
									<Card key={key} size="small">
										<Row gutter={[12, 8]} align="middle">
											<Col span={24} md={10}>
												<Form.Item
													{...restField}
													name={[name, "rule"]}
													label="Rule"
													rules={[
														{
															required: true,
															message: "Rule text is required",
														},
													]}
													style={{ marginBottom: 0 }}
												>
													<Input.TextArea
														rows={2}
														placeholder="e.g. Never use the phrase 'in conclusion'"
													/>
												</Form.Item>
											</Col>
											<Col span={12} md={6}>
												<Form.Item
													{...restField}
													name={[name, "category"]}
													label="Category"
													style={{ marginBottom: 0 }}
												>
													<Select
														options={NEGATIVE_RULE_CATEGORIES.map((o) => ({
															...o,
														}))}
													/>
												</Form.Item>
											</Col>
											<Col span={8} md={4}>
												<Form.Item
													{...restField}
													name={[name, "active"]}
													label="Active"
													valuePropName="checked"
													style={{ marginBottom: 0 }}
												>
													<Switch />
												</Form.Item>
											</Col>
											<Col span={4} md={4}>
												<Button
													type="text"
													danger
													icon={<DeleteOutlined />}
													onClick={() => remove(name)}
													style={{ marginTop: 22 }}
												/>
											</Col>
										</Row>
									</Card>
								))}
								<Button
									type="dashed"
									onClick={() =>
										add({
											id: crypto.randomUUID(),
											rule: "",
											category: "general",
											active: true,
										})
									}
									block
									icon={<PlusOutlined />}
								>
									Add Rule
								</Button>
							</Space>
						)}
					</Form.List>
				</Card>
			),
		},
		{
			key: "quality_thresholds",
			label: "Quality",
			children: (
				<Card bordered={false} className="bg-warm-bg">
					<Paragraph type="secondary" style={{ marginBottom: 24 }}>
						Override default quality thresholds for this tenant. Leave word count
						fields empty to use system defaults.
					</Paragraph>
					<Row gutter={[24, 16]}>
						<Col span={24} md={12}>
							<Form.Item
								name={["quality_thresholds", "min_word_count_override"]}
								label="Min Word Count Override"
								extra="Leave empty for system default."
							>
								<InputNumber
									min={100}
									max={10000}
									style={{ width: "100%" }}
									placeholder="System default"
								/>
							</Form.Item>
						</Col>
						<Col span={24} md={12}>
							<Form.Item
								name={["quality_thresholds", "max_word_count_override"]}
								label="Max Word Count Override"
								extra="Leave empty for system default."
							>
								<InputNumber
									min={100}
									max={10000}
									style={{ width: "100%" }}
									placeholder="System default"
								/>
							</Form.Item>
						</Col>
						<Col span={24}>
							<Form.Item
								name={["quality_thresholds", "target_content_score"]}
								label="Target Content Score"
								extra="Content below this score triggers a warning."
							>
								<Slider
									min={0}
									max={100}
									marks={{ 0: "0", 50: "50", 80: "80", 100: "100" }}
								/>
							</Form.Item>
						</Col>
						<Col span={24} md={12}>
							<Form.Item
								name={["quality_thresholds", "require_featured_image"]}
								label="Require Featured Image"
								valuePropName="checked"
							>
								<Switch />
							</Form.Item>
						</Col>
					</Row>
				</Card>
			),
		},
		{
			key: "learned_patterns",
			label: `Learned Patterns${patterns?.length ? ` (${patterns.length})` : ""}`,
			children: (
				<Card bordered={false} className="bg-warm-bg">
					<Paragraph type="secondary" style={{ marginBottom: 24 }}>
						Patterns learned automatically from approved AI fix corrections.
						Patterns with 5+ occurrences and 80%+ confidence are injected into
						future content generation. Toggle inactive to stop a pattern from
						being used.
					</Paragraph>
					{patternsLoading ? (
						<div className="flex justify-center py-8">
							<img
								src={loadingAnimation}
								alt="Loading"
								className="dark:invert w-20 h-auto"
							/>
						</div>
					) : !patterns?.length ? (
						<Alert
							message="No patterns yet. Patterns are created when AI fixes are approved on content feedback."
							type="info"
						/>
					) : (
						<Space direction="vertical" style={{ width: "100%" }} size={12}>
							{patterns.map((pattern) => (
								<Card
									key={pattern.id}
									size="small"
									style={{
										opacity: pattern.is_active ? 1 : 0.6,
									}}
								>
									<Row gutter={[16, 8]} align="middle">
										<Col span={24} md={10}>
											<Text strong>{pattern.description}</Text>
											<br />
											<Text type="secondary" style={{ fontSize: 12 }}>
												Type: {pattern.comment_type} | Created:{" "}
												{dayjs(pattern.created_at).format("MMM D, YYYY")}
											</Text>
										</Col>
										<Col span={8} md={4}>
											<Tooltip title="Number of times this pattern was observed">
												<Text type="secondary" style={{ fontSize: 12 }}>
													Occurrences
												</Text>
												<br />
												<Text strong>{pattern.occurrence_count}</Text>
											</Tooltip>
										</Col>
										<Col span={8} md={4}>
											<Tooltip title="Approval rate (80%+ required for auto-injection)">
												<Text type="secondary" style={{ fontSize: 12 }}>
													Confidence
												</Text>
												<br />
												<Progress
													percent={Math.round(pattern.confidence_score * 100)}
													size="small"
													status={
														pattern.confidence_score >= 0.8
															? "success"
															: "normal"
													}
													style={{ width: 80 }}
												/>
											</Tooltip>
										</Col>
										<Col span={4} md={3}>
											{pattern.auto_apply_eligible ? (
												<Tag
													icon={<CheckCircleOutlined />}
													color="success"
												>
													Auto-inject
												</Tag>
											) : (
												<Tag
													icon={<CloseCircleOutlined />}
													color="default"
												>
													Below threshold
												</Tag>
											)}
										</Col>
										<Col span={4} md={3}>
											<Tooltip
												title={
													pattern.is_active
														? "Click to deactivate"
														: "Click to reactivate"
												}
											>
												<Switch
													checked={pattern.is_active}
													onChange={(checked) =>
														togglePatternMutation.mutate({
															patternId: pattern.id,
															isActive: checked,
														})
													}
													loading={togglePatternMutation.isPending}
												/>
											</Tooltip>
										</Col>
									</Row>
								</Card>
							))}
						</Space>
					)}
				</Card>
			),
		},
		{
			key: "history",
			label: `History (v${prefsData?.version ?? 0})`,
			children: (
				<Card bordered={false} className="bg-warm-bg">
					<Paragraph type="secondary" style={{ marginBottom: 24 }}>
						Version history of preference changes. Each save creates a new
						version. Rolling back creates a new version with the preferences
						from the target version.
					</Paragraph>
					{historyLoading ? (
						<div className="flex justify-center py-8">
							<img
								src={loadingAnimation}
								alt="Loading"
								className="dark:invert w-20 h-auto"
							/>
						</div>
					) : !history?.length ? (
						<Alert
							message="No history yet. Save preferences to create the first version."
							type="info"
						/>
					) : (
						<Timeline
							mode="left"
							items={[...history].reverse().map((entry) => ({
								color: entry.change_source === "rollback" ? "red" : "blue",
								children: (
									<Card size="small">
										<Space
											direction="vertical"
											size={4}
											style={{ width: "100%" }}
										>
											<div
												style={{
													display: "flex",
													justifyContent: "space-between",
													alignItems: "center",
												}}
											>
												<Tag
													color={
														entry.change_source === "rollback" ? "red" : "blue"
													}
												>
													v{entry.version}
												</Tag>
												<Text type="secondary">
													{dayjs(entry.created_at).format("MMM D, YYYY h:mm A")}
												</Text>
											</div>
											<Text>{entry.change_summary}</Text>
											<Space size={4} wrap>
												{entry.changed_fields
													.filter((f) => f !== "_rollback")
													.map((f) => (
														<Tag key={f}>{f}</Tag>
													))}
											</Space>
											<div
												style={{
													display: "flex",
													justifyContent: "space-between",
													alignItems: "center",
												}}
											>
												<Text type="secondary">by {entry.changed_by}</Text>
												{entry.version !== prefsData?.version && (
													<Button
														size="small"
														icon={<UndoOutlined />}
														onClick={() => handleRollback(entry.version)}
														loading={rollbackMutation.isPending}
													>
														Rollback
													</Button>
												)}
											</div>
										</Space>
									</Card>
								),
							}))}
						/>
					)}
				</Card>
			),
		},
	];

	// ── Render ──

	return (
		<div style={{ position: "relative", minHeight: "70vh" }}>
		<div
			aria-hidden={locked || undefined}
			style={{
				maxWidth: 1200,
				margin: "0 auto",
				padding: "24px 16px 96px",
				...(locked
					? { filter: "blur(5px)", pointerEvents: "none", userSelect: "none" }
					: {}),
			}}
		>
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "flex-start",
					marginBottom: 24,
				}}
			>
				<Space direction="vertical" size={2}>
					<Space align="center">
						<Title level={2} style={{ margin: 0 }}>
							Content Preferences
						</Title>
						{prefsData?.version ? (
							<Tag color="blue">v{prefsData.version}</Tag>
						) : (
							<Tag>Defaults</Tag>
						)}
					</Space>
					<Text type="secondary">
						Configure writing style, tone, and generation rules for{" "}
						<strong>{currentTenant}</strong>
					</Text>
				</Space>
				{isDirty && (
				<Button
					type="primary"
					size="large"
					onClick={handleSubmit}
					loading={updateMutation.isPending}
				>
					Save Preferences
				</Button>
			)}
			</div>

			{saveStatus && (
				<Alert
					message={saveStatus}
					type="success"
					showIcon
					closable
					onClose={() => setSaveStatus(null)}
					style={{ marginBottom: 16 }}
				/>
			)}

			<PreferenceDiscoveryPanel
				form={form}
				currentPreferences={prefsData?.preferences}
				onApplyChanges={() => setIsDirty(true)}
			/>

			<ManagedInBrandBible
				header={COPY.brandBible.deferralHeader}
				items={[
					{
						concept: COPY.brandBible.concepts.tone,
						value: brandBible?.tone_of_writing,
						section: COPY.brandBible.sections.voiceMessaging,
					},
					{
						concept: COPY.brandBible.concepts.avoidWords,
						value:
							brandBible?.brand_restrictions?.words_to_avoid ??
							brandBible?.brand_restrictions?.words_avoid,
						section: COPY.brandBible.sections.guardrails,
					},
				]}
			/>

			<Form
				layout="vertical"
				form={form}
				onValuesChange={() => setIsDirty(true)}
			>
				<Tabs
					activeKey={activeTab}
					onChange={handleTabChange}
					items={tabItems}
					type="card"
					size="large"
				/>
			</Form>

			{isDirty && (
				<div
					style={{
						position: "fixed",
						left: 0,
						right: 0,
						bottom: 16,
						display: "flex",
						justifyContent: "center",
						pointerEvents: "none",
						zIndex: 100,
					}}
				>
					<div
						style={{
							background: "#fff",
							border: "1px solid #f0f0f0",
							boxShadow: "0 8px 24px rgba(15, 23, 42, 0.15)",
							borderRadius: 12,
							padding: "12px 24px",
							display: "flex",
							gap: 12,
							alignItems: "center",
							pointerEvents: "auto",
						}}
					>
						<ExclamationCircleFilled style={{ color: "#faad14" }} />
						<Text>You have unsaved changes</Text>
						<Button onClick={handleDiscard}>Discard</Button>
						<Button
							type="primary"
							onClick={handleSubmit}
							loading={updateMutation.isPending}
						>
							Save all changes
						</Button>
					</div>
				</div>
			)}
		</div>

			{locked && (
				<div
					style={{
						position: "absolute",
						inset: 0,
						zIndex: 10,
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						gap: 12,
						padding: 24,
						textAlign: "center",
						backgroundColor: "rgba(253, 250, 247, 0.55)",
						backdropFilter: "blur(2px)",
						WebkitBackdropFilter: "blur(2px)",
					}}
				>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							width: 56,
							height: 56,
							borderRadius: "9999px",
							backgroundColor: "#fff",
							boxShadow: "0 4px 14px rgba(0,0,0,0.12)",
						}}
					>
						<LockOutlined style={{ fontSize: 26, color: "var(--color-coral)" }} />
					</div>
					<Title level={4} style={{ margin: 0 }}>
						Content Preferences is locked
					</Title>
					<Text type="secondary" style={{ maxWidth: 440 }}>
						This section isn't connected to the content generation pipeline
						yet, so changes here wouldn't take effect. It'll be unlocked once
						it's ready.
					</Text>
				</div>
			)}
		</div>
	);
};

export { ContentPreferencesPage };
export default ContentPreferencesPage;
